import React, { useState, useEffect } from 'react';
import { useLocation } from 'react-router-dom';
import { text } from '../text';
import { hooks } from '../hooks';
import { components } from '../components';
import { theme } from '../constants';
import { useLanguage } from '../context/LanguageContext';
import axiosInstance from '../services/axiosInstance';
import { getCurrentLocation } from '../utils/locationUtils';
import { MdDelete, MdAdd, MdKeyboardArrowUp, MdKeyboardArrowDown, MdCheckBox, MdCheckBoxOutlineBlank, MdMyLocation } from 'react-icons/md';

const DeleteIcon = MdDelete as any;
const AddIcon = MdAdd as any;
const ArrowUpIcon = MdKeyboardArrowUp as any;
const ArrowDownIcon = MdKeyboardArrowDown as any;
const CheckBoxIcon = MdCheckBox as any;
const CheckBoxOutlineBlankIcon = MdCheckBoxOutlineBlank as any;
const MyLocationIcon = MdMyLocation as any;

const DAY_NAMES = ['Minggu', 'Senin', 'Selasa', 'Rabu', 'Kamis', "Jum'at", 'Sabtu'];
const dayName = (dateStr: string) => {
  if (!dateStr) return '';
  const d = new Date(dateStr + 'T00:00:00');
  return DAY_NAMES[d.getDay()];
};

interface WorkItem {
  work_date: string;
  description: string;
  // Penanda kapal (task di grup perjalanan) — '' = bersama
  task_id: string;
}

const emptyItem = (): WorkItem => ({ work_date: '', description: '', task_id: '' });

interface GroupTask { id: number; vessel_name: string | null; assignees: { id: number; name: string }[] }

export const TimeSheetForm: React.FC = () => {
  const navigate = hooks.useAppNavigate();
  const { state } = useLocation();
  const { t } = useLanguage();
  const ts = (t as any).timeSheet;
  const task = state?.task;
  const existingTimeSheet = state?.timeSheet;
  const isEdit = !!existingTimeSheet;

  const tg = (t as any).tripGroup || {};
  // Task asal time sheet (time sheet bersama bisa dibuka dari task lain di grup perjalanan)
  const ownerTaskId = existingTimeSheet?.task_id || task?.id;
  const [groupTasks, setGroupTasks] = useState<GroupTask[]>([]);
  const [isShared, setIsShared] = useState<boolean>(!!existingTimeSheet?.is_shared);
  const inGroup = groupTasks.length > 1;
  const showVessel = inGroup && isShared;

  useEffect(() => {
    if (!ownerTaskId) return;
    axiosInstance.get(`/task-groups/task/${ownerTaskId}`)
      .then(r => setGroupTasks(r.data?.group ? (r.data.tasks || []) : []))
      .catch(() => {});
  }, [ownerTaskId]);

  // Time sheet bersama: kapal = semua kapal grup, teknisi = gabungan assignee grup
  const vesselName = showVessel
    ? Array.from(new Set(groupTasks.map(g => g.vessel_name).filter(Boolean))).join(', ') || '-'
    : (existingTimeSheet && !existingTimeSheet.is_shared ? existingTimeSheet.vessel_name : null) || task?.vessel_name || '-';
  const ownAssignees: any[] = task?.assigned_to || [];
  const assignees: any[] = showVessel
    ? Array.from(new Map(groupTasks.flatMap(g => g.assignees || []).map(u => [u.id, u])).values())
    : (groupTasks.find(g => g.id === Number(ownerTaskId))?.assignees || ownAssignees);
  const vesselOptions = [
    { value: '', label: tg.vesselShared || 'Bersama' },
    ...groupTasks.map(g => ({ value: String(g.id), label: g.vessel_name || `Task #${g.id}` })),
  ];

  const [location, setLocation] = useState(existingTimeSheet?.location || '');
  const [items, setItems] = useState<WorkItem[]>(
    existingTimeSheet?.items?.length
      ? existingTimeSheet.items.map((i: any) => ({ work_date: String(i.work_date).substring(0, 10), description: i.description, task_id: i.task_id ? String(i.task_id) : '' }))
      : [emptyItem()]
  );
  const [selectedTechnicianIds, setSelectedTechnicianIds] = useState<number[]>(
    (existingTimeSheet?.technician_user_ids || []).map((n: any) => Number(n))
  );
  const [usedTechnicianIds, setUsedTechnicianIds] = useState<Set<number>>(new Set());
  const [loadingUsed, setLoadingUsed] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [errorMsg, setErrorMsg] = useState('');
  const [gpsAddress, setGpsAddress] = useState('');
  const [fetchingGps, setFetchingGps] = useState(false);
  const [gpsError, setGpsError] = useState('');

  useEffect(() => {
    if (!ownerTaskId) { setLoadingUsed(false); return; }
    axiosInstance.get(`/time-sheets/task/${ownerTaskId}`)
      .then(res => {
        const used = new Set<number>();
        (res.data || []).forEach((entry: any) => {
          if (isEdit && entry.id === existingTimeSheet.id) return;
          if (Number(entry.task_id) !== Number(ownerTaskId)) return;
          (entry.technician_user_ids || []).forEach((uid: any) => used.add(Number(uid)));
        });
        setUsedTechnicianIds(used);
      })
      .catch(() => {})
      .finally(() => setLoadingUsed(false));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [ownerTaskId]);

  const handleGetLocation = async () => {
    setGpsError('');
    setFetchingGps(true);
    try {
      const locationData = await getCurrentLocation({ addressFormat: 'full' });
      setGpsAddress(locationData.address || `${locationData.lat}, ${locationData.lng}`);
    } catch (error: any) {
      setGpsError(error.message || ts?.locationFetchError || 'Gagal mengambil lokasi. Pastikan GPS aktif dan izin lokasi diberikan.');
    } finally {
      setFetchingGps(false);
    }
  };

  const toggleTechnician = (id: number) => {
    if (usedTechnicianIds.has(id)) return;
    setSelectedTechnicianIds(prev => prev.includes(id) ? prev.filter(x => x !== id) : [...prev, id]);
  };

  const updateItem = (idx: number, field: keyof WorkItem, val: string) => {
    setItems(prev => {
      const next = [...prev];
      next[idx] = { ...next[idx], [field]: val };
      return next;
    });
  };

  const addRow = () => setItems(prev => [...prev, emptyItem()]);
  const removeRow = (idx: number) => setItems(prev => prev.filter((_, i) => i !== idx));
  const moveItem = (idx: number, dir: -1 | 1) => {
    setItems(prev => {
      const newIdx = idx + dir;
      if (newIdx < 0 || newIdx >= prev.length) return prev;
      const next = [...prev];
      [next[idx], next[newIdx]] = [next[newIdx], next[idx]];
      return next;
    });
  };

  const handleSubmit = async () => {
    setErrorMsg('');
    if (selectedTechnicianIds.length === 0) {
      setErrorMsg(ts?.errorTechnician || 'Pilih minimal 1 teknisi');
      return;
    }
    if (!location.trim()) {
      setErrorMsg(ts?.errorLocation || 'Lokasi wajib diisi');
      return;
    }
    const validItems = items
      .filter(i => i.work_date && i.description.trim())
      .map(i => ({ work_date: i.work_date, description: i.description, task_id: showVessel && i.task_id ? Number(i.task_id) : null }));
    if (validItems.length === 0) {
      setErrorMsg(ts?.errorItems || 'Tambahkan minimal 1 baris pekerjaan');
      return;
    }
    if (!task?.id && !isEdit) {
      setErrorMsg(ts?.errorTask || 'Task tidak ditemukan');
      return;
    }

    setSubmitting(true);
    try {
      if (isEdit) {
        await axiosInstance.put(`/time-sheets/${existingTimeSheet.id}`, {
          location: location.trim(),
          items: JSON.stringify(validItems),
          technician_user_ids: selectedTechnicianIds,
          is_shared: inGroup ? isShared : !!existingTimeSheet.is_shared,
        });
      } else {
        await axiosInstance.post(`/time-sheets/task/${task.id}`, {
          location: location.trim(),
          items: JSON.stringify(validItems),
          technician_user_ids: selectedTechnicianIds,
          is_shared: inGroup && isShared,
        });
      }
      navigate(-1);
    } catch (err: any) {
      setErrorMsg(err.response?.data?.message || ts?.errorSave || 'Gagal menyimpan time sheet');
    } finally {
      setSubmitting(false);
    }
  };

  const inputStyle: React.CSSProperties = {
    width: '100%',
    border: '1px solid #D1D5DB',
    borderRadius: 10,
    padding: '10px 14px',
    fontSize: 14,
    boxSizing: 'border-box',
    outline: 'none',
    fontFamily: 'inherit',
    color: theme.colors.mainDark,
    backgroundColor: '#fff',
  };

  const labelStyle: React.CSSProperties = { color: '#6B7280', marginBottom: 5, display: 'block', fontSize: 12 };
  const readonlyBoxStyle: React.CSSProperties = {
    width: '100%',
    borderRadius: 10,
    padding: '10px 14px',
    fontSize: 14,
    boxSizing: 'border-box',
    backgroundColor: '#F3F4F6',
    color: '#374151',
  };

  return (
    <div id="screen" style={{ backgroundColor: '#F9FAFB', minHeight: '100vh' }}>
      <components.Header title={isEdit ? (ts?.editTitle || 'Edit Laporan Pekerjaan') : (ts?.addTitle || 'Laporan Pekerjaan')} goBack={true} />

      <main className="container" style={{ marginTop: 60, paddingBottom: 110, height: 'calc(100vh - 60px)', overflowY: 'auto' }}>
        <div style={{ padding: '10px 8px', display: 'flex', flexDirection: 'column', gap: 12 }}>

          {errorMsg && (
            <div style={{ backgroundColor: '#FEE2E2', border: '1px solid #FECACA', borderRadius: 10, padding: '10px 14px' }}>
              <text.T14 style={{ color: '#DC2626' }}>{errorMsg}</text.T14>
            </div>
          )}

          {inGroup && (
            <components.SharedDocToggle checked={isShared} onChange={setIsShared} label={tg.shareToggle} hint={tg.shareHint} />
          )}

          {/* Task info */}
          <div style={{ backgroundColor: '#fff', borderRadius: 14, padding: 14, border: '1px solid #E5E7EB' }}>
            <div style={{ marginBottom: 10 }}>
              <label style={labelStyle}>{ts?.vessel || 'Nama Kapal'}</label>
              <div style={readonlyBoxStyle}>{vesselName}</div>
            </div>
            <div style={{ marginBottom: 10 }}>
              <label style={labelStyle}>{ts?.technician || 'Nama Teknisi'}</label>
              {loadingUsed ? (
                <div style={readonlyBoxStyle}>{t.common.loading || 'Memuat...'}</div>
              ) : (
                <div style={{ border: '1px solid #E5E7EB', borderRadius: 10, overflow: 'hidden' }}>
                  {assignees.map((u: any, idx: number) => {
                    const isUsed = usedTechnicianIds.has(u.id);
                    const isSelected = selectedTechnicianIds.includes(u.id);
                    return (
                      <div
                        key={u.id}
                        onClick={() => toggleTechnician(u.id)}
                        style={{
                          display: 'flex', alignItems: 'center', gap: 10, padding: '10px 14px',
                          cursor: isUsed ? 'not-allowed' : 'pointer',
                          opacity: isUsed ? 0.45 : 1,
                          borderBottom: idx < assignees.length - 1 ? '1px solid #F3F4F6' : 'none',
                          backgroundColor: isSelected ? '#EFF6FF' : '#fff',
                        }}
                      >
                        {isSelected ? <CheckBoxIcon size={20} color={theme.colors.mainColor} /> : <CheckBoxOutlineBlankIcon size={20} color="#9CA3AF" />}
                        <text.T14 style={{ color: theme.colors.mainDark, flex: 1 }}>{u.name}</text.T14>
                        {isUsed && (
                          <text.T10 style={{ color: '#9CA3AF' }}>{ts?.technicianUsed || 'Sudah ada time sheet'}</text.T10>
                        )}
                      </div>
                    );
                  })}
                </div>
              )}
            </div>
            <div>
              <label style={labelStyle}>{ts?.location || 'Lokasi'}</label>
              <input
                value={location}
                onChange={e => setLocation(e.target.value)}
                placeholder={ts?.locationPlaceholder || 'Contoh: Dermaga Utara'}
                style={inputStyle}
              />

              <div
                onClick={fetchingGps ? undefined : handleGetLocation}
                style={{
                  marginTop: 8, display: 'flex', alignItems: 'center', gap: 6,
                  cursor: fetchingGps ? 'default' : 'pointer', opacity: fetchingGps ? 0.6 : 1,
                  width: 'fit-content',
                }}
              >
                <MyLocationIcon size={16} color={theme.colors.mainColor} />
                <text.T12 style={{ color: theme.colors.mainColor, fontWeight: '600' }}>
                  {fetchingGps ? (ts?.fetchingLocation || 'Mengambil lokasi...') : (ts?.getLocation || 'Ambil Lokasi GPS')}
                </text.T12>
              </div>

              {gpsError && (
                <text.T12 style={{ color: '#DC2626', marginTop: 6, display: 'block' }}>{gpsError}</text.T12>
              )}

              {!!gpsAddress && (
                <div style={{
                  marginTop: 8, backgroundColor: '#F0FDF4', border: '1px solid #BBF7D0', borderRadius: 10,
                  padding: '8px 12px', display: 'flex', alignItems: 'center', gap: 10,
                }}>
                  <text.T12 style={{ color: '#166534', flex: 1 }}>{gpsAddress}</text.T12>
                  <div
                    onClick={() => setLocation(gpsAddress)}
                    style={{
                      backgroundColor: '#16A34A', borderRadius: 8, padding: '5px 12px', cursor: 'pointer', flexShrink: 0,
                    }}
                  >
                    <text.T12 style={{ color: '#fff', fontWeight: '600' }}>{ts?.apply || 'Gunakan'}</text.T12>
                  </div>
                </div>
              )}
            </div>
          </div>

          {/* Work rows */}
          <div>
            <text.T14 style={{ fontWeight: '600', color: '#374151', marginBottom: 8, display: 'block' }}>
              {ts?.workItems || 'Rincian Pekerjaan'}
            </text.T14>

            {items.map((item, idx) => (
              <div key={idx} style={{ backgroundColor: '#fff', border: '1px solid #E5E7EB', borderRadius: 14, marginBottom: 8, overflow: 'hidden' }}>
                <div style={{ backgroundColor: '#EFF6FF', padding: '8px 14px', display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                  <text.T12 style={{ fontWeight: '700', color: '#3B82F6' }}>#{idx + 1}</text.T12>
                  <div style={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                    <div
                      onClick={() => idx > 0 && moveItem(idx, -1)}
                      style={{ cursor: idx > 0 ? 'pointer' : 'default', padding: 4, opacity: idx > 0 ? 1 : 0.3 }}
                    >
                      <ArrowUpIcon size={18} color="#3B82F6" />
                    </div>
                    <div
                      onClick={() => idx < items.length - 1 && moveItem(idx, 1)}
                      style={{ cursor: idx < items.length - 1 ? 'pointer' : 'default', padding: 4, opacity: idx < items.length - 1 ? 1 : 0.3 }}
                    >
                      <ArrowDownIcon size={18} color="#3B82F6" />
                    </div>
                    {items.length > 1 && (
                      <div onClick={() => removeRow(idx)} style={{ cursor: 'pointer', padding: 4 }}>
                        <DeleteIcon size={16} color="#EF4444" />
                      </div>
                    )}
                  </div>
                </div>

                <div style={{ padding: '10px 14px', display: 'flex', flexDirection: 'column', gap: 10 }}>
                  <div>
                    <label style={labelStyle}>
                      {ts?.date || 'Tanggal'}{item.work_date ? ` — ${dayName(item.work_date)}` : ''}
                    </label>
                    <input
                      type="date"
                      value={item.work_date}
                      onChange={e => updateItem(idx, 'work_date', e.target.value)}
                      style={inputStyle}
                    />
                  </div>
                  {showVessel && (
                    <div>
                      <label style={labelStyle}>{tg.vessel || 'Kapal'}</label>
                      <select value={item.task_id} onChange={e => updateItem(idx, 'task_id', e.target.value)} style={inputStyle}>
                        {vesselOptions.map(o => <option key={o.value} value={o.value}>{o.label}</option>)}
                      </select>
                    </div>
                  )}
                  <div>
                    <label style={labelStyle}>{ts?.description || 'Deskripsi Pekerjaan'}</label>
                    <textarea
                      value={item.description}
                      onChange={e => updateItem(idx, 'description', e.target.value)}
                      placeholder={ts?.descriptionPlaceholder || 'Contoh: Analisa kerusakan sensor'}
                      rows={2}
                      style={{ ...inputStyle, resize: 'vertical', fontFamily: 'inherit' }}
                    />
                  </div>
                </div>
              </div>
            ))}

            <div
              onClick={addRow}
              style={{ padding: '12px 16px', display: 'flex', alignItems: 'center', gap: 8, cursor: 'pointer', border: '1.5px dashed #93C5FD', borderRadius: 12, backgroundColor: '#EFF6FF' }}
            >
              <AddIcon size={18} color="#3B82F6" />
              <text.T14 style={{ color: '#3B82F6' }}>{ts?.addRow || 'Tambah Baris'}</text.T14>
            </div>
          </div>

        </div>
      </main>

      {/* Bottom button */}
      <div style={{
        position: 'fixed', bottom: 0, left: 0, right: 0,
        display: 'flex', justifyContent: 'center',
      }}>
        <div style={{
          width: '100%', maxWidth: 650,
          backgroundColor: '#fff', borderTop: '1px solid #F3F4F6',
          padding: '10px 16px 14px',
        }}>
          <components.Button
            title={submitting ? (t.common.submitting || 'Mengirim...') : (isEdit ? (ts?.saveChanges || 'Simpan Perubahan') : (ts?.submit || 'Kirim'))}
            onClick={submitting ? undefined : handleSubmit}
            containerStyle={{ width: '100%', opacity: submitting ? 0.6 : 1 }}
          />
        </div>
      </div>
    </div>
  );
};
